import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search } from 'lucide-react';
import { statusMeta } from '../orders/orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ title, items, render }) => items.length > 0 && (
  <Card className="border-border">
    <CardHeader className="pb-2"><CardTitle className="text-sm">{title} ({items.length})</CardTitle></CardHeader>
    <CardContent className="pt-0 space-y-1.5">{items.map(render)}</CardContent>
  </Card>
);

export default function AdminSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/search`, { params: { q: q.trim() } })
        .then((r) => setResults(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const empty = results && Object.values(results).every((v) => v.length === 0);

  return (
    <AdminLayout title="Recherche universelle" subtitle="Dossiers, patients, praticiens, cabinets, laboratoires, factures, certificats, fichiers">
      <div className="relative max-w-xl mb-5">
        <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
        <Input autoFocus className="pl-9 h-10" placeholder="JPD-2026-..., Dupont, facture JCF, fichier STL..."
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="universal-search-input" />
      </div>
      {empty && <p className="text-sm text-muted-foreground">Aucun résultat pour « {q} ».</p>}
      {results && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="search-results">
          <Section title="📁 Dossiers" items={results.orders} render={(o) => (
            <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between text-xs hover:underline">
              <span><span className="font-mono font-semibold">{o.order_number}</span> · {o.patient_last_name || ''} · {o.cabinet_name}</span>
              <Badge variant="outline" className={`${statusMeta(o.status).cls} text-[10px]`}>{statusMeta(o.status).label}</Badge>
            </Link>)} />
          <Section title="🏢 Cabinets & laboratoires" items={results.organizations} render={(o) => (
            <div key={o.id} className="text-xs">{o.type === 'cabinet' ? '🦷' : '🏭'} {o.name}</div>)} />
          <Section title="🧾 Factures client" items={results.client_invoices} render={(i) => (
            <Link key={i.id} to="/admin/invoicing" className="flex justify-between text-xs hover:underline">
              <span className="font-mono">{i.invoice_number}</span>
              <span className="text-muted-foreground">{i.cabinet_name} · {i.total?.toFixed(2)} €</span>
            </Link>)} />
          <Section title="📄 Factures fournisseur" items={results.supplier_invoices} render={(i) => (
            <Link key={i.id} to="/admin/invoicing" className="flex justify-between text-xs hover:underline">
              <span className="font-mono">{i.invoice_number}</span>
              <span className="text-muted-foreground">{i.fabricant_name}</span>
            </Link>)} />
          <Section title="✅ Certificats" items={results.certificates} render={(c) => (
            <Link key={c.order_id} to={`/admin/orders/${c.order_id}`} className="flex justify-between text-xs hover:underline">
              <span className="font-mono">{c.order_number}</span>
              <span className="text-muted-foreground">{c.fabricant_name}</span>
            </Link>)} />
          <Section title="📎 Fichiers" items={results.files} render={(f) => (
            <Link key={f.id} to={`/admin/orders/${f.order_id}`} className="flex justify-between text-xs hover:underline">
              <span className="truncate">{f.original_name}</span>
              <span className="font-mono text-muted-foreground">{f.order_number}</span>
            </Link>)} />
        </div>
      )}
    </AdminLayout>
  );
}
